import React, { Component } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { Circle, MapContext, Polygon } from '@react-google-maps/api';
import {
  LocationDto,
  OrderDto,
  PlantDto,
  UrlKeyDto,
  GeoZoneDto,
  RouteDto,
  StatusEventDto,
  PositionDto,
  ProductLine,
  UserRegionDto,
} from '@trucktrax/trucktrax-ts-common';
import { postGISToGoogleMapsPoints, vars } from '@trucktrax/trucktrax-common';

import { connect } from 'react-redux';

import { DefaultLocation } from './mapTypes';
import * as constants from '../../../constants/mapConstants';
import RecenterMapBtn from './RecenterMapBtn';
import icons from './icons';
import { PinLocationInfo } from '../../../store/actions/mapviewActions';
import {
  DEFAULT_RADIUS,
  DROP_PIN_ICON,
  METERS_PER_MILE,
  DEFAULT_MAP_ZOOM,
  MAX_MAP_ZOOM,
  DEFAULT_GEOFENCE_ZOOM,
} from '../../../constants/mapConstants';

import { MAP_TYPES, MapMarkerType, ZoneType } from '../../../types';
import { GEOZONE_TYPE, GEOZONE_SHAPE_STYLE } from '../../../constants/appConstants';
import LegendMapKey from './LegendMapKey';
import {
  getRouteHistoryLegendStatuses,
  retrieveProductLineNameDiff,
} from '../../../util/statusesUtil';
import { getRoutesOwnership } from '../../../util/routeHistoryUtil';
import { ReduxState } from '../../../store';
import styles from './RouteHistoryTTMap.module.css';
import TTMarker from './markers/TTMarker';
import PositionMarker from './markers/PositionMarker';
import PlantMarkerWithInfoPane from './markers/PlantMarkerWithInfoPane';
import markerColors from './markers/MarkerColors';
import {
  Z_INDEX_MARKER_TOP,
  Z_INDEX_MARKER_MIDDLE,
  Z_INDEX_MARKER_BOTTOM,
  Z_INDEX_PIN_MARKER_DEFAULT,
} from './markers/markerConstants';
import { determineRegionId, isEmptyString, noop } from '../../../util/appUtil';
import { filterActiveMarkersByRegion, PositionMarkerDataInfo } from '../../../util/markerUtil';
import { createIconElement } from './markers/iconUtils';
import { isRouteHistoryMarkerOpen, PopupToggleMap } from '../../../store/reducers/mapReducers';
import { CustomDrawingManager, useAdvancedMarker } from './TraxMapDrawingManager';
import { MarkerClustererContext } from './TTDrawingManager';

import {
  areLocationsDifferent,
  isLatLngLiteral,
  isValidLatLng,
  isValidLocation,
  locationToLatLng,
  mapsToLocationDto,
} from '../../../util/geoUtil';
import recordLog, { LogLevel } from '../../../util/errorUtil';
import config from 'react-global-configuration';
import mapStyles from './map-style';

import { MarkerClusterer } from '@googlemaps/markerclusterer';

const SVG_MARKER_MAX = 500;

interface MapChangeEvent {
  location?: LocationDto;
  circle?: {
    center: LocationDto;
    radius: number;
  };
  polygon?: {
    points: LocationDto[];
  };
}

type ShapeStyles = {
  fillColor: string;
  strokeColor: string;
  fillOpacity: number;
  strokeWeight: number;
  clickable: boolean;
  zIndex: number;
  draggable: boolean;
  editable: boolean;
};

type routeDataDto = {
  route: RouteDto;
  markerIdx: number;
};

// Advanced Marker wrapper for pin functionality
interface PinMarkerProps {
  map?: google.maps.Map;
  position?: google.maps.LatLngLiteral;
  visible?: boolean;
}

const PinMarker: React.FC<PinMarkerProps> = ({ map, position, visible = true }) => {
  // Create custom pin content
  const pinContent = React.useMemo(() => {
    const div = document.createElement('div');
    const iconConfig = {
      path: icons.pinPath,
      fillColor: vars.blue,
      strokeColor: vars.white,
      strokeWeight: 1,
      scale: 1,
    };
    const iconElement = createIconElement(iconConfig, 1);
    if (iconElement) {
      div.appendChild(iconElement);
    }
    return div;
  }, []);

  useAdvancedMarker({
    map,
    position: position || { lat: 0, lng: 0 },
    content: pinContent,
    visible: visible && !!position,
    draggable: false,
    zIndex: Z_INDEX_PIN_MARKER_DEFAULT,
  });

  return null;
};

export class RouteHistoryTTMap extends Component<RouteHistoryTTMapProps, RouteHistoryTTMapState> {
  circleRef?: google.maps.Circle;

  defaultZoom = 9;

  listenersRef?: google.maps.MapsEventListener[];

  mapRef: React.RefObject<HTMLDivElement>;

  map?: google.maps.Map;

  polygonRef?: google.maps.Polygon;

  // Circle for the pin selection
  pinCircle: google.maps.Circle | null = null;

  private pinCircleDragEndListener: google.maps.MapsEventListener | null = null;

  private lastGeocodedPosition: google.maps.LatLngLiteral | null = null;

  private markerClusterer?: MarkerClusterer;

  gmapControlPositions = google.maps.ControlPosition;

  mapPositions = {
    leftBottom: this.gmapControlPositions.LEFT_BOTTOM,
    rightBottom: this.gmapControlPositions.RIGHT_BOTTOM,
    topRight: this.gmapControlPositions.TOP_RIGHT,
  };

  private containerStyle = {
    width: '100%',
    height: '100%',
  };

  private recenterButtonRoot?: Root;

  private legendRoot?: Root;

  get hasRouteHistoryProductLines() {
    return this.props.isRouteHistory && (this.props.routeHistoryProductLines ?? []).length > 0;
  }

  constructor(props: RouteHistoryTTMapProps) {
    super(props);
    this.state = {
      isFirstMapRender: true,
      isMapHasBeenBounded: false,
      currentZoom: DEFAULT_MAP_ZOOM,
      currentBounds: null,
      pinMarkerPosition: null,
      canMovePinCircle: false,
      pauseMapRendering: false,
    };

    this.mapRef = React.createRef<HTMLDivElement>();
  }

  /** brings last selected to top and the rest of selected above unselected */
  static calculateZIndex = (
    lastMarkerSelected: string | null,
    truckId: string,
    isSelected: boolean,
    maxIdx: number,
  ) => {
    const [top, middle, bottom] = [
      Z_INDEX_MARKER_TOP + maxIdx,
      Z_INDEX_MARKER_MIDDLE + maxIdx,
      Z_INDEX_MARKER_BOTTOM,
    ];
    if (lastMarkerSelected && lastMarkerSelected === truckId && isSelected) {
      return top;
    }
    return isSelected ? middle : bottom;
  };

  private propsAtLastRender?: RouteHistoryTTMapProps;

  shouldComponentUpdate(
    _nextProps: Readonly<RouteHistoryTTMapProps>,
    nextState: Readonly<RouteHistoryTTMapState>,
  ): boolean {
    if (!this.map || !nextState.pauseMapRendering) {
      return true;
    }
    this.propsAtLastRender ??= this.props;
    return false;
  }

  componentDidMount() {
    this.initializeMap();
    this.reCenterMap();
    const { activePinMap } = this.props;
    if (!isValidLocation(activePinMap)) {
      return;
    }
    const position = locationToLatLng(activePinMap);
    this.placeMarkerAndPan(position);
  }

  componentDidUpdate(latestPrevProps: RouteHistoryTTMapProps) {
    const prevProps = this.propsAtLastRender ?? latestPrevProps;
    this.propsAtLastRender = undefined;
    const { markers, currentRegion, routeHistoryProductLines, routeHistoryList, activePinMap } =
      this.props;

    if (this.props.pinMap !== prevProps.pinMap) {
      const isPinActive = this.props.pinMap === true ? DROP_PIN_ICON : undefined;
      this.map?.setOptions({ draggableCursor: isPinActive });
    }

    const pinRemoved =
      activePinMap?.removePin === true && prevProps.activePinMap?.removePin !== true;
    const locationRemoved =
      activePinMap &&
      isEmptyString(activePinMap.location) &&
      activePinMap.location !== prevProps.activePinMap?.location;
    if (locationRemoved || pinRemoved) {
      this.clearPinOverlays();
    }

    const activePinLocationChanged =
      isValidLocation(activePinMap) && areLocationsDifferent(activePinMap, prevProps.activePinMap);
    const pinPosition = locationToLatLng(activePinMap);
    const canUpdatePinLocation =
      activePinLocationChanged && areLocationsDifferent(this.lastGeocodedPosition, pinPosition);
    if (canUpdatePinLocation) {
      this.placeMarkerAndPan(pinPosition);
    }
    const currentRouteTimes = routeHistoryList?.map(r => r.route?.[0]?.createdTimestamp ?? '0');
    const priorRouteTimes = prevProps.routeHistoryList?.map(
      r => r.route?.[0]?.createdTimestamp ?? '0',
    );
    const routeTimesChanged =
      currentRouteTimes?.length !== priorRouteTimes?.length ||
      currentRouteTimes?.some((time, index) => time !== priorRouteTimes?.[index]);
    if (routeHistoryList && routeHistoryList.length > 0 && routeTimesChanged) {
      this.map?.setZoom(12);
    }
    if (
      markers?.length !== prevProps.markers?.length ||
      currentRegion?.url !== prevProps.currentRegion?.url
    ) {
      this.reCenterMap();
    }

    // add history legend updates. the result set matters too: which statuses the
    // legend lists depends on whether the routes are internal, external or both
    if (
      routeHistoryProductLines !== prevProps.routeHistoryProductLines ||
      this.props.routeHistoryList !== prevProps.routeHistoryList
    ) {
      this.renderHistoryLegend();
    }

    // check the if the pin circle size changed
    if (this.props.isRouteHistory && this.pinCircle) {
      const storePinRadiusMeters = this.getPinRadiusMilesNumber() * METERS_PER_MILE;
      const currentPinRadius = this.pinCircle.getRadius();
      if (storePinRadiusMeters !== currentPinRadius) {
        this.pinCircle.setRadius(storePinRadiusMeters);
      }
    }
  }

  initializeMap(): google.maps.Map | undefined {
    if (!window.google || !window.google.maps) {
      recordLog(LogLevel.Warn, 'Google Maps API not loaded yet!');
      return undefined;
    }

    const mapId =
      process.env.REACT_APP_GOOGLE_MAP_ID || config.get('REACT_APP_GOOGLE_MAP_ID') || undefined;
    recordLog(LogLevel.Debug, '[TTMap] Initializing map with Map ID:', {
      mapId,
      fromEnv: process.env.REACT_APP_GOOGLE_MAP_ID,
      fromConfig: config.get('REACT_APP_GOOGLE_MAP_ID'),
    });

    const { center } = this.props;
    const centerOption: google.maps.MapOptions = isLatLngLiteral(center) ? { center } : {};
    this.map = new window.google.maps.Map(this.mapRef.current as HTMLDivElement, {
      ...centerOption,
      zoom: DEFAULT_MAP_ZOOM,
      streetViewControl: false,
      streetViewControlOptions: {
        position: google.maps.ControlPosition.RIGHT_BOTTOM,
      },
      mapTypeControl: true,
      fullscreenControl: false,
      rotateControl: false,
      controlSize: constants.GM_CONTROLSIZE,
      mapId,
      clickableIcons: false,
      mapTypeControlOptions: {
        mapTypeIds: [MAP_TYPES.ROADMAP, MAP_TYPES.SATELLITE, MAP_TYPES.HYBRID],
        position: this.mapPositions.topRight,
      },
      styles: process.env.REACT_APP_GOOGLE_MAP_ID ? undefined : mapStyles,
      disableDoubleClickZoom: false,
    });

    this.registerMapEvents(this.map);
    this.renderHistoryLegend();
    this.renderRecenterMapButton();

    this.markerClusterer = new MarkerClusterer({ map: this.map });
  }

  private getPinRadiusMilesNumber = () => {
    const miles = Number(this.props.pinRadiusMiles);
    return Number.isFinite(miles) ? miles : DEFAULT_RADIUS;
  };

  clearPinOverlays = () => {
    this.pinCircle = null;
    this.lastGeocodedPosition = null;
    this.setState({ pinMarkerPosition: null, canMovePinCircle: false });
  };

  replacePinOverlay = (position: google.maps.LatLngLiteral) => {
    this.pinCircle = null;
    this.lastGeocodedPosition = null;
    this.setState({ pinMarkerPosition: null }, () => {
      this.setState({ pinMarkerPosition: position });
    });
  };

  componentWillUnmount() {
    this.handleMapUnmountCleanUp();

    if (this.recenterButtonRoot) {
      this.recenterButtonRoot.unmount();
    }
    if (this.legendRoot) {
      this.legendRoot.unmount();
    }
    this.markerClusterer?.setMap(null);
  }

  handleMapUnmountCleanUp = () => {
    try {
      this.markerClusterer?.clearMarkers();
    } catch (e) {
      recordLog(LogLevel.Debug, '[RouteHistoryTTMap] Error removing markers', { error: e });
    }

    try {
      const googleMapRef = window.google?.maps;
      if (googleMapRef && this.map) {
        googleMapRef.event.clearInstanceListeners(this.map);
      }
    } catch (e) {
      recordLog(LogLevel.Debug, '[RouteHistoryTTMap] Error removing map listeners on unmount', {
        error: e,
      });
    }
  };

  onPinCircleLoad = (circle: google.maps.Circle) => {
    this.pinCircle = circle;
    this.pinCircleDragEndListener = this.pinCircle.addListener('dragend', this.onPinCenterChanged);
  };

  onPinCircleUnmount = () => {
    this.pinCircleDragEndListener?.remove();
    this.pinCircleDragEndListener = null;
    this.pinCircle = null;
  };

  renderPlants = () =>
    this.props.plantList &&
    this.props.plantList.map(plant => {
      // the chosen color will be determined based on plant id, so that plants show up with a stable consistent color
      const colorIndex = plant.id % markerColors.length;
      return (
        <PlantMarkerWithInfoPane
          key={plant.id}
          plant={plant}
          mapRef={this.map}
          markerColor={markerColors[colorIndex]}
          updateToFieldValueWithPlantDrivers={noop}
          onClick={noop}
        />
      );
    });

  renderRecenterMapButton = () => {
    if (!this.map) return;
    const controlButtonDiv = document.createElement('div');
    this.recenterButtonRoot = createRoot(controlButtonDiv);
    this.recenterButtonRoot.render(<RecenterMapBtn onClick={this.reCenterMap} />);
    this.map.controls[this.mapPositions.rightBottom].push(controlButtonDiv);
  };

  /**
   * The legend should only explain statuses these results can actually reach,
   * which depends on whether the routes are ours or an outside hauler's. With no
   * routes to describe there's nothing to narrow by, so returns undefined and
   * lets the legend list the full product-line key.
   */
  static getLegendStatusOverrides = (routeHistoryList: RouteDto[] | null) => {
    const ownership = getRoutesOwnership(routeHistoryList);
    if (!ownership) {
      return undefined;
    }
    return {
      aggregate: getRouteHistoryLegendStatuses(ProductLine.Aggregates, ownership),
      cement: getRouteHistoryLegendStatuses(ProductLine.Cement, ownership),
      readyMix: getRouteHistoryLegendStatuses(ProductLine.ReadyMix, ownership),
    };
  };

  renderHistoryLegend = () => {
    if (!this.map) return;
    const { routeHistoryProductLines } = this.props;
    if (!this.hasRouteHistoryProductLines) {
      this.map.controls[this.mapPositions.leftBottom].clear();
      return;
    }

    try {
      // gets the Legend Map Key component
      const controlButtonDiv = document.createElement('div');
      this.legendRoot = createRoot(controlButtonDiv);
      this.legendRoot.render(<LegendMapKey productLines={routeHistoryProductLines!} />);

      this.map.controls[this.mapPositions.leftBottom].clear();
      this.map.controls[this.mapPositions.leftBottom].push(controlButtonDiv);
    } catch (ex: unknown) {
      recordLog(LogLevel.Debug, 'Encountered error rendering history legend');
    }
  };

  zoomToBounds = () => {
    if (!this.map) {
      return;
    }

    let boundsNumber = 0;
    const bounds = new google.maps.LatLngBounds();

    const extendBounds = (lat?: number, lng?: number): number => {
      if (!isValidLatLng(lat, lng)) return 0;
      // At this point, lat and lng are guaranteed to be numbers
      const point = new google.maps.LatLng(lat as number, lng as number);
      bounds.extend(point);
      return 1;
    };

    const { markers, currentRegion } = this.props;

    if (!this.state.isFirstMapRender) return;

    const validMarkerList = filterActiveMarkersByRegion(markers ?? [], currentRegion.url ?? '');

    // This code shows the map center somehow
    const position =
      validMarkerList.length === 1 ? validMarkerList[0].location : currentRegion.location;
    const { latitude, longitude } = position || { latitude: 0, longitude: 0 };
    boundsNumber += extendBounds(latitude, longitude);
    // no need to increment boundsNumber a second time
    if (latitude !== undefined && longitude !== undefined) {
      extendBounds(latitude + 0.0003, longitude + 0.0003);
    }

    if (boundsNumber <= 1) {
      const center = bounds.getCenter();
      // Validate center coordinates before using them
      if (center && isValidLatLng(center.lat(), center.lng())) {
        this.map.setCenter(center);
        this.map.setZoom(this.defaultZoom);
      }
    } else {
      this.map.fitBounds(bounds);
    }
    this.setState({ isFirstMapRender: false });
  };

  reCenterMap = () => {
    this.setState({ isFirstMapRender: true }, this.zoomToBounds);
  };

  handleCenterChange = () => {
    const center = this.map?.getCenter()?.toJSON();
    if (center && this.props.onCenterChange) {
      this.props.onCenterChange(center);
    }
  };

  prevChange = {};

  onChange(change: MapChangeEvent) {
    if (!this.map) return;
    if (JSON.stringify(this.prevChange) === JSON.stringify(change)) {
      return;
    }

    this.prevChange = change;
    if (this.props.onChange !== undefined) {
      this.props.onChange(change);
    }
  }

  onCircleLoad = (circle: google.maps.Circle) => {
    this.circleRef = circle;
    if (!this.state.isMapHasBeenBounded) {
      this.setState({ isMapHasBeenBounded: true }, () => this.zoomToBoundsCP());
    }
  };

  onCircleUnmount = () => {
    this.listenersRef = [];
    this.circleRef = undefined;
  };

  onCircleChanged = () => {
    if (!this.map || !this.circleRef) return;
    this.onCircleComplete(this.circleRef);
  };

  onCircleComplete = (circle?: google.maps.Circle) => {
    if (!this.map || !circle) return;
    const center = mapsToLocationDto(circle.getCenter());
    this.onChange({
      circle: {
        center,
        radius: circle.getRadius(),
      },
      polygon: undefined,
    });
  };

  private geozoneCircleCenter: google.maps.LatLngLiteral | null = null;

  getGeozoneCircleCenter = (lat: number, lng: number): google.maps.LatLngLiteral => {
    const cached = this.geozoneCircleCenter;
    if (cached && cached.lat === lat && cached.lng === lng) {
      return cached;
    }
    this.geozoneCircleCenter = { lat, lng };
    return this.geozoneCircleCenter;
  };

  renderGeozoneCircle = (shapeStyles: ShapeStyles) => {
    const zone = this.props.currentGeozone?.zone;
    if (!zone?.circle?.center?.latitude || !zone?.circle?.center?.longitude) {
      return undefined;
    }

    return (
      <Circle
        key="route-map-geozone-circle"
        center={this.getGeozoneCircleCenter(
          zone.circle.center.latitude,
          zone.circle.center.longitude,
        )}
        onLoad={this.onCircleLoad}
        radius={zone.circle.radius}
        onRadiusChanged={this.onCircleChanged}
        onCenterChanged={this.onCircleChanged}
        onUnmount={this.onCircleUnmount}
        options={{ ...shapeStyles, map: this.map }}
      />
    );
  };

  onPolygonChanged = () => {
    if (!this.polygonRef) return;
    this.onPolygonComplete(this.polygonRef);
  };

  // Clean up refs
  onPolygonUnmount = () => {
    if (this.listenersRef) {
      this.listenersRef.forEach(lis => lis.remove());
    }
    this.listenersRef = [];
    this.polygonRef = undefined;
  };

  onPolygonComplete = (polygon: google.maps.Polygon) => {
    const newPolygon = polygon
      .getPath()
      .getArray()
      .map(point => ({
        lat: point.lat(),
        lng: point.lng(),
      }));
    const points = RouteHistoryTTMap.googleMapsToPostGISPoints(newPolygon) ?? [];
    this.onChange({
      circle: undefined,
      polygon: {
        points,
      },
    });
  };

  static googleMapsToPostGISPoints = (points?: google.maps.LatLngLiteral[]) => {
    if (!points) return null;

    const closedPoints = [...points];
    closedPoints.push(points[0]);
    return closedPoints.map(point => ({
      latitude: point.lat,
      longitude: point.lng,
    }));
  };

  getGeofenceStyle(zoneType?: ZoneType) {
    const shapeStyles: ShapeStyles = {
      fillColor: '#67B4F6',
      strokeColor: '#67B4F6',
      fillOpacity: 0.25,
      strokeWeight: 2,
      clickable: false,
      zIndex: 1,
      draggable: true,
      editable: this.props.isEditable ?? false,
    };

    type GeozoneType = typeof GEOZONE_SHAPE_STYLE;
    type GeozoneShapeStyle = {
      fillColor: string;
      strokeColor: string;
      zIndex: number;
    };

    const zone: { [geozoneName: string]: GeozoneShapeStyle } = {};

    zone[GEOZONE_TYPE.PLANT] = {
      ...GEOZONE_SHAPE_STYLE[GEOZONE_TYPE.PLANT as keyof GeozoneType],
      zIndex: 3,
    };
    zone[GEOZONE_TYPE.INQUEUE] = {
      ...GEOZONE_SHAPE_STYLE[GEOZONE_TYPE.INQUEUE as keyof GeozoneType],
      zIndex: 2,
    };
    zone[GEOZONE_TYPE.RETURN] = {
      ...GEOZONE_SHAPE_STYLE[GEOZONE_TYPE.RETURN as keyof GeozoneType],
      zIndex: 1,
    };

    return zoneType ? { ...shapeStyles, ...zone[zoneType] } : shapeStyles;
  }

  onPolygonLoad = (polygon: google.maps.Polygon) => {
    this.polygonRef = polygon;
    if (!this.listenersRef) this.listenersRef = [];
    const path = polygon.getPath();
    this.listenersRef.forEach(lis => lis.remove());
    this.listenersRef = [
      path.addListener('set_at', this.onPolygonChanged),
      path.addListener('insert_at', this.onPolygonChanged),
      path.addListener('remove_at', this.onPolygonChanged),
    ];

    if (!this.state.isMapHasBeenBounded) {
      this.setState({ isMapHasBeenBounded: true }, () => this.zoomToBoundsCP());
    }
  };

  zoomToBoundsCP = () => {
    if (!this.map) return;
    const bounds = new google.maps.LatLngBounds();

    const { currentGeozone } = this.props;
    const hasValidGeozone =
      (currentGeozone &&
        currentGeozone?.location &&
        isValidLatLng(currentGeozone.location.latitude, currentGeozone.location.longitude)) ??
      false;
    if (hasValidGeozone) {
      const { latitude, longitude } = currentGeozone!.location!;
      const lat = latitude ?? 0;
      const lng = longitude ?? 0;

      bounds.extend(new google.maps.LatLng(lat - 0.001, lng - 0.001));
      bounds.extend(new google.maps.LatLng(lat + 0.001, lng + 0.001));
    }

    if (this.circleRef) {
      const circleBounds = this.circleRef.getBounds();
      if (circleBounds) {
        bounds.union(circleBounds);
      }
    }

    if (this.polygonRef) {
      this.polygonRef
        .getPath()
        .getArray()
        .forEach(point => {
          bounds.extend(point);
        });
    }

    this.map.fitBounds(bounds);
  };

  generateGeozonePolygon = (shapeStyles: ShapeStyles) => {
    const zone = this.props.currentGeozone?.zone;
    return (
      zone?.polygon &&
      zone?.polygon.points && (
        <Polygon
          key="route-map-geozone-polygon"
          path={postGISToGoogleMapsPoints(this.props.currentGeozone?.zone?.polygon?.points) as any}
          onLoad={this.onPolygonLoad}
          editable
          // Event used when dragging the whole Polygon
          onDragEnd={this.onPolygonChanged}
          // Event used when manipulating and adding points
          onMouseUp={this.onPolygonChanged}
          onUnmount={this.onPolygonUnmount}
          options={{ ...shapeStyles, map: this.map }}
        />
      )
    );
  };

  onRouteMarkerClick = (marker: MarkerType) => () => this.props.onRouteMarkerClick?.(marker);

  onMarkerClose = (marker: MarkerType) => () => this.props.onMarkerClose?.(marker);

  processGeocodeReply = (results: any, status: google.maps.GeocoderStatus) => {
    if (status === google.maps.GeocoderStatus.OK) {
      if (results !== null && results[0]) {
        // Updates the value in the storage.
        // Uses lastGeocodedPosition the actual point clicked when available
        // instead of the geocoder's snapped result
        if (this.props.onPinChanged) {
          this.props.onPinChanged({
            pinMode: false,
            pinDropped: true,
            location: results[0].formatted_address,
            lat: this.lastGeocodedPosition?.lat ?? results[0].geometry.location.lat(),
            lng: this.lastGeocodedPosition?.lng ?? results[0].geometry.location.lng(),
          });
        }
      }
    }
  };

  onRadiusChanged = () => {
    if (!this.pinCircle) return;
    // Extracts the radius value in miles
    const radius = this.pinCircle.getRadius() / METERS_PER_MILE;
    // Updates the value in the storage
    if (this.props.onPinRadiusChanged) {
      this.props.onPinRadiusChanged(radius.toString());
    }
  };

  placeMarkerAndPan = (position: google.maps.LatLngLiteral) => {
    if (!this.map) {
      return;
    }
    this.onMarkerPlaced(position);
    this.map.panTo(position);
    const { currentZoom } = this.state;
    const zoom = currentZoom < DEFAULT_GEOFENCE_ZOOM ? DEFAULT_GEOFENCE_ZOOM : currentZoom;
    this.updateZoom(zoom);
  };

  updateZoom = (zoom: number) => {
    if (!this.map) {
      return;
    }
    const { currentZoom } = this.state;
    if (zoom === currentZoom) {
      return;
    }
    this.setState({ currentZoom: zoom });
    this.map.setZoom(zoom);
  };

  /**
   * Handles marker placement from the custom drawing manager
   * @param {google.maps.LatLngLiteral} position The position where the marker was placed
   */
  onMarkerPlaced = (position: google.maps.LatLngLiteral) => {
    this.replacePinOverlay(position);
    this.lastGeocodedPosition = position;
    new google.maps.Geocoder().geocode({ location: position }, this.processGeocodeReply);
  };

  /**
   * Handles circle drawing completion from the custom drawing manager
   * @param {Object} circleData The circle data with center and radius
   */
  onCircleDrawingComplete = (circleData: { center: google.maps.LatLngLiteral; radius: number }) => {
    const center: LocationDto = {
      latitude: circleData.center.lat,
      longitude: circleData.center.lng,
    };

    this.onChange({
      circle: {
        center,
        radius: circleData.radius,
      },
      polygon: undefined,
    });
  };

  /**
   * Handles polygon drawing completion from the custom drawing manager
   * @param {Object} polygonData The polygon data with points
   */
  onPolygonDrawingComplete = (polygonData: { points: google.maps.LatLngLiteral[] }) => {
    const points = RouteHistoryTTMap.googleMapsToPostGISPoints(polygonData.points) ?? [];
    this.onChange({
      circle: undefined,
      polygon: {
        points,
      },
    });
  };

  /**
   * Legacy drop pin mode method (kept for compatibility but now uses custom drawing manager)
   * @param {any} mapsMouseEvent mouse map click event with the information of the location
   */
  dropPinMode = (mapsMouseEvent: any) => {
    // Convert to the format expected by the new marker placement handler
    const position = {
      lat: mapsMouseEvent.latLng.lat(),
      lng: mapsMouseEvent.latLng.lng(),
    };

    this.placeMarkerAndPan(position);

    // Reset the map cursor
    if (this.map) {
      this.map.setOptions({ draggableCursor: undefined });
    }
  };

  static getRouteHistoryPositionStatus = (position: PositionDto, statuses: StatusEventDto[]) => {
    // if the statuses have only one, returns the position 0
    if (statuses.length === 1) return statuses[0].status;
    // gets the truckStatus
    const truckStatus = statuses.filter((s: StatusEventDto) => s.timestamp === position.timestamp);
    // if the truck status has the exact timestamp returns the status value
    if (truckStatus.length > 0) {
      return truckStatus[0].status;
    }
    // the timestamp can be in a range between all the statuses, loop through all of them to get the status
    for (let i = 0; i < statuses.length; ) {
      if (statuses[i] !== undefined && statuses[i + 1] !== undefined) {
        const currentTime = statuses[i].timestamp as string;
        const nextTime = statuses[i + 1].timestamp as string;
        const positionTime = position.timestamp as string;
        if (currentTime < positionTime && nextTime > positionTime) {
          return statuses[i].status;
        }
      }
      i += 1;
    }

    // the for loop above will not apply a status for the final status in the list, because
    // statuses[i + 1] will always be undefined when statuses[i] is the final status in the list
    // so let's handle the final status below
    if (statuses.length > 0) {
      const lastStatusInList = statuses[statuses.length - 1];
      const lastStatusTime = lastStatusInList.timestamp;

      if (lastStatusTime && position.timestamp && position.timestamp > lastStatusTime) {
        return lastStatusInList.status;
      }
    }

    // Default status if don't find one
    return 'Unknown';
  };

  routeIsSelected = (route: RouteDto) =>
    this.props.selectedRouteHistoryList?.includes(route.id) ?? false;

  routeIsHovered = (route: RouteDto) => this.props.routeHistoryHoverSelection?.id === route.id;

  hoveredRouteOrAllRoutes = (route: RouteDto) => {
    if (this.props.routeHistoryHoverSelection) {
      return this.routeIsHovered(route);
    }

    // no route is hovered, so all routes are returned
    return true;
  };

  private static computeZoomVisibility(
    positions: PositionDto[],
    statuses: StatusEventDto[],
    zoom: number,
  ): boolean[] {
    if (positions.length === 0) {
      return [];
    }

    const statusesByTruckUrl = new Map<string, StatusEventDto[]>();
    for (const status of statuses) {
      const url = status.truck?.url;
      if (!url) {
        continue;
      }
      const list = statusesByTruckUrl.get(url);
      if (list) {
        list.push(status);
      } else {
        statusesByTruckUrl.set(url, [status]);
      }
    }

    const positionStatuses = positions.map((pos: PositionDto) => {
      const url = (pos.trackedObject as any)?.url;
      const trackedStatuses = url ? (statusesByTruckUrl.get(url) ?? []) : [];
      return RouteHistoryTTMap.getRouteHistoryPositionStatus(pos, trackedStatuses);
    });

    const anchors = new Set<number>();
    let groupStart = 0;
    for (let i = 1; i <= positions.length; i++) {
      if (i === positions.length || positionStatuses[i] !== positionStatuses[groupStart]) {
        const groupEnd = i - 1;
        const groupMid = groupStart + Math.floor((groupEnd - groupStart) / 2);
        anchors.add(groupStart);
        anchors.add(groupMid);
        anchors.add(groupEnd);
        groupStart = i;
      }
    }

    const zoomDelta = Math.max(0, MAX_MAP_ZOOM - Math.floor(zoom));
    const step = Math.pow(2, Math.floor(zoomDelta / 3));

    return positions.map((_, idx) => anchors.has(idx) || idx % step === 0);
  }

  renderPositionMarkers = (route: RouteDto, startIdx: number, totalMarkers: number) => {
    const positions = route.route;

    const shouldUsePerformanceImprovements = totalMarkers > SVG_MARKER_MAX;
    const visibility = shouldUsePerformanceImprovements
      ? RouteHistoryTTMap.computeZoomVisibility(positions, route.statuses, this.state.currentZoom)
      : null;

    const { currentBounds } = this.state;
    return positions.map((position: PositionDto, idx: number) => {
      const positionId = position.id as string;

      const isPositionVisible = !visibility || visibility[idx];
      const isInBounds =
        !currentBounds ||
        currentBounds.contains({
          lat: position.location?.latitude ?? 0,
          lng: position.location?.longitude ?? 0,
        });
      if (shouldUsePerformanceImprovements && (!isPositionVisible || !isInBounds)) {
        return null;
      }

      const currentRegionId = determineRegionId(this.props.currentRegion as UserRegionDto);
      const popupToggleMap = this.props.popupToggleMap as PopupToggleMap;
      const isOpen = this.props.routeHistoryProductLines?.some(productLine =>
        isRouteHistoryMarkerOpen(
          popupToggleMap,
          currentRegionId,
          productLine as ProductLine,
          positionId,
        ),
      );

      const trackedTruckStatuses = route.statuses.filter(
        (s: StatusEventDto) => s.truck?.url === position.trackedObject.url,
      );

      const truckDetails = {
        ...position,
        trackedObject: {
          id: position.trackedObject,
          truckAlias: route.truck.truckAlias,
        },
        status: RouteHistoryTTMap.getRouteHistoryPositionStatus(position, trackedTruckStatuses),
      };

      const { lastMarkerSelected } = popupToggleMap;
      const zIndex =
        startIdx +
        idx +
        RouteHistoryTTMap.calculateZIndex(
          lastMarkerSelected,
          positionId,
          isOpen ?? false,
          totalMarkers,
        );
      const markerData: MarkerDataType = {
        markerId: positionId,
        type: MapMarkerType.Truck,
        driverDetails: position.driver,
        truckDetails,
        ticket: position.ticket!,
        productLine: position.productLine,
      };
      const marker: MarkerType = {
        markerId: positionId,
        type: MapMarkerType.Truck,
        markerData,
      };

      return (
        <PositionMarker
          displayPNG={shouldUsePerformanceImprovements}
          parentRouteId={route.id}
          isRouteHistory
          key={positionId}
          zIndex={zIndex}
          mapRef={this.map}
          position={truckDetails}
          onClick={this.onRouteMarkerClick(marker)}
          onCloseClick={this.onMarkerClose(marker)}
          markerData={{
            driverDetails: {
              firstName: route.truck.driverName,
            },
            truckDetails,
            ticket: route.ticket,
            order: route.order,
          }}
          markerStatusMap={retrieveProductLineNameDiff(position.productLine)}
          hideLabel
          isSelected={isOpen}
          canHover
        />
      );
    });
  };

  static renderTableRow = (header: string, value: string | undefined) => {
    const val = value === '' ? undefined : value; // empty strings have no place here
    return (
      <tr>
        <th>{header}</th>
        <td>{val ?? 'N/A'}</td>
      </tr>
    );
  };

  static renderRouteInfoAsTable = (route: RouteDto) => {
    const { order, ticket, truck } = route;
    return (
      <table className={styles.routePopup}>
        <colgroup>
          <col className={styles.labelColumn} />
          <col className={styles.contentColumn} />
        </colgroup>
        <tbody>
          {RouteHistoryTTMap.renderTableRow('Order', order?.orderNumber)}
          {RouteHistoryTTMap.renderTableRow('Ticket', ticket?.ticketNumber)}
          {RouteHistoryTTMap.renderTableRow('Driver', truck?.driverName)}
        </tbody>
      </table>
    );
  };

  static findCenterPosition = (positions: PositionDto[]): PositionDto | null => {
    if (!positions || positions.length === 0) return null;
    const centerIndex = Math.floor(positions.length / 2);
    return positions[centerIndex!];
  };

  renderHoveredRouteInfo = (route: RouteDto) => {
    // exit if this route is not hovered
    if (!this.routeIsHovered(route)) return null;

    // get the "middle" position of the route as an anchor point
    const centerPosition = RouteHistoryTTMap.findCenterPosition(route.route);
    // exit if there aren't enough positions to use as an anchor
    if (centerPosition === null) return null;

    const { latitude, longitude } = centerPosition.location!;
    const position = {
      latitude,
      longitude,
      lat: latitude,
      lng: longitude,
    };

    const icon = {
      path: icons.circlePath,
      fillColor: vars.blue,
      fillOpacity: 0,
      strokeWeight: 0,
      strokeColor: vars.white,
      scale: 1,
    };

    const pixelOffset: google.maps.Size = {
      width: 0,
      height: 0,
      equals: other => other?.width === 0 && other.height === 0,
    };

    return (
      <TTMarker
        key={`marker${0}`}
        mapRef={this.map}
        zIndex={999}
        icon={icon}
        position={position}
        pixelOffset={pixelOffset}
        infoPaneTitle={`Truck #${route.truck.alias}`}
        infoPaneContent={RouteHistoryTTMap.renderRouteInfoAsTable(route)}
        isSelected
        isLabelHover
        isRouteHistory={this.props.isRouteHistory}
        manualInfoPosition
      />
    );
  };

  buildRouteHistoryMarkers = () => {
    const routes = (this.props.routeHistoryList ?? [])
      .filter(this.routeIsSelected) // discard deselected routes first
      .filter(this.hoveredRouteOrAllRoutes);

    let totalMarkers = 0;
    const routeData: Array<routeDataDto> = [];
    routes.forEach(route => {
      const positionCount = route.route.length;
      const routeDatum: routeDataDto = {
        route,
        markerIdx: totalMarkers,
      };
      routeData.push(routeDatum);
      totalMarkers += positionCount;
    });

    const data = routeData.map(data => (
      <RouteHistory key={data.route.id}>
        {this.renderHoveredRouteInfo(data.route) /* will only render if a route is hovered */}
        {this.renderPositionMarkers(data.route, data.markerIdx, totalMarkers)}
      </RouteHistory>
    ));
    return (
      <MarkerClustererContext.Provider
        key="route-clusterer"
        value={totalMarkers > SVG_MARKER_MAX ? (this.markerClusterer ?? null) : null}
      >
        {data}
      </MarkerClustererContext.Provider>
    );
  };

  registerMapEvents(map: google.maps.Map | undefined): void {
    if (!map) {
      return;
    }

    google.maps.event.addListenerOnce(map, 'tilesloaded', () => {
      this.setState({
        currentZoom: map.getZoom() ?? this.state.currentZoom,
        currentBounds: map.getBounds() ?? null,
      });
      map.addListener('zoom_changed', () => {
        const zoom = map.getZoom();
        if (zoom !== undefined && zoom !== this.state.currentZoom) {
          this.setState({ currentZoom: zoom, pauseMapRendering: true });
        }
      });

      map.addListener('idle', () => {
        const { currentBounds, pauseMapRendering } = this.state;
        const bounds = map.getBounds() ?? null;
        const boundsChanged = bounds !== null && !bounds.equals(currentBounds);
        if (!boundsChanged && !pauseMapRendering) {
          return;
        }
        this.setState({
          pauseMapRendering: false,
          currentBounds: boundsChanged ? bounds : currentBounds,
        });
      });

      map.addListener('drag', () => {
        if (!this.state.pauseMapRendering) {
          this.setState({ pauseMapRendering: true });
        }
      });

      map.addListener('click', (event: unknown) => {
        if (this.props.pinMap) {
          this.dropPinMode(event);
        }
      });

      map.addListener('dragend', () => this.handleCenterChange());
    });
  }

  mapMarkers = (): (JSX.Element | JSX.Element[] | undefined)[] | undefined => {
    if (!this.map) {
      return;
    }
    const geoFenceStyle = this.getGeofenceStyle(this.props.geofenceAreaType);
    const markers = [
      this.renderGeozoneCircle(geoFenceStyle),
      this.generateGeozonePolygon(geoFenceStyle),
      this.buildRouteHistoryMarkers(),
      this.renderPlants(),
    ];

    if (this.props.isEditable) {
      markers.push(
        <CustomDrawingManager
          map={this.map}
          onMarkerComplete={this.onMarkerPlaced}
          onCircleComplete={this.onCircleDrawingComplete}
          onPolygonComplete={this.onPolygonDrawingComplete}
          isEditable={this.props.isEditable}
          showControls
          controlPosition={google.maps.ControlPosition.TOP_LEFT}
        />,
      );
    }

    const { pinMarkerPosition } = this.state;
    if (pinMarkerPosition) {
      const { canMovePinCircle } = this.state;
      markers.push(
        <PinMarker
          map={this.map}
          position={pinMarkerPosition}
          visible={!canMovePinCircle}
          key="route-map-pin"
        />,
      );
      markers.push(
        <Circle
          key="route-map-pin-circle"
          visible
          center={pinMarkerPosition}
          radius={this.getPinRadiusMilesNumber() * METERS_PER_MILE}
          options={{
            strokeColor: canMovePinCircle ? vars.blue : vars.orange,
            strokeWeight: canMovePinCircle ? 2 : 1,
            fillColor: canMovePinCircle ? vars.blue : vars.orange,
            fillOpacity: 0.1,
            editable: canMovePinCircle,
            draggable: canMovePinCircle,
            clickable: true,
            map: this.map,
          }}
          onLoad={this.onPinCircleLoad}
          onUnmount={this.onPinCircleUnmount}
          onRadiusChanged={this.onRadiusChanged}
          onRightClick={this.togglePinDragging}
        />,
      );
    }

    return markers;
  };

  togglePinDragging = () => {
    if (!this.pinCircle) {
      return;
    }

    const { canMovePinCircle: previous } = this.state;
    this.setState({ canMovePinCircle: !previous });
    if (previous) {
      const coords = this.pinCircle.getCenter();
      const position = mapsToLocationDto(coords);
      this.updatePinCenter(position);
    }
  };

  onPinCenterChanged = (center?: google.maps.MapMouseEvent) => {
    if (!center) {
      return;
    }

    const { latLng: coords } = center;
    if (!coords) {
      return;
    }

    const position = mapsToLocationDto(coords);
    this.updatePinCenter(position);
  };

  updatePinCenter = (pinMarkerPosition?: LocationDto): void => {
    if (!this.state.canMovePinCircle || !pinMarkerPosition) {
      return;
    }

    if (!isValidLocation(pinMarkerPosition)) {
      return;
    }

    this.onMarkerPlaced(pinMarkerPosition);
  };

  render() {
    const markers = this.mapMarkers() ?? [];
    return (
      <MapContext.Provider value={this.map ?? null}>
        <div ref={this.mapRef} style={this.containerStyle} id="route-map" key="route-map-key" />
        {markers}
      </MapContext.Provider>
    );
  }
}

interface RegionWithLocation extends UrlKeyDto {
  location?: LocationDto;
}

export interface MarkerDataType {
  markerId: string;
  type: string;
  driverDetails: any;
  truckDetails?: PositionMarkerDataInfo;
  ticket: UrlKeyDto;
  status?: string;
  productLine?: string;
}

export interface MarkerType {
  markerId: string;
  type: string;
  markerData: OrderDto | MarkerDataType | PlantDto;
}

export function mapStateToProps(state: ReduxState) {
  return {
    routeHistoryHoverSelection: state.routeHistoryHoverSelection,
    pinRadiusMiles: state.pinRadiusMiles,
    plantList: state.plantList,
    popupToggleMap: state.popupToggleMap,
  };
}

type ReduxProps = ReturnType<typeof mapStateToProps>;

export interface OwnProps {
  polygon?: {
    points: LocationDto[];
  };
  currentRegion: RegionWithLocation;
  markers?: PositionMarkerDataInfo[];
  center?: DefaultLocation;
  onCenterChange?: (center: google.maps.LatLngLiteral) => void;
  onRouteMarkerClick?: (marker: MarkerType) => void;
  onMarkerClose?: (marker: MarkerType) => void;
  currentGeozone?: GeoZoneDto;
  geofenceAreaType?: ZoneType;
  isEditable?: boolean;
  onChange?: (evt: MapChangeEvent) => void;
  pinMap?: boolean;
  activePinMap?: PinLocationInfo;
  onPinChanged?: (pinInfo: PinLocationInfo) => void;
  onPinRadiusChanged?: (radius: string) => void;
  isRouteHistory?: boolean;
  routeHistoryProductLines?: string[];
  routeHistoryList: RouteDto[] | null;
  selectedRouteHistoryList?: number[];
}

export interface RouteHistoryTTMapState {
  isFirstMapRender: boolean;
  isMapHasBeenBounded: boolean;
  currentZoom: number;
  currentBounds: google.maps.LatLngBounds | null;
  pinMarkerPosition: google.maps.LatLngLiteral | null;
  canMovePinCircle: boolean;
  pauseMapRendering: boolean;
}

export type RouteHistoryTTMapProps = OwnProps & ReduxProps;

export default connect(mapStateToProps)(RouteHistoryTTMap);

export const RouteHistory: React.FunctionComponent<{ children?: React.ReactNode }> = props => (
  <div className="route-history">{props.children}</div>
);
